/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { BazaarvoiceConfig } from './types';

export const BV_LOADER_HOST = 'https://apps.bazaarvoice.com';
export const BV_API_HOST_STAGING = 'https://display-stg.ugc.bazaarvoice.com';
export const BV_API_HOST_PRODUCTION = 'https://display.ugc.bazaarvoice.com';
export const BV_SEO_HOST_STAGING = 'https://seo-stg.bazaarvoice.com';
export const BV_SEO_HOST_PRODUCTION = 'https://seo.bazaarvoice.com';

export const BV_DEFAULT_ZONE = 'Main Site';
export const BV_DEFAULT_LOCALE = 'en_US';
export const BV_PIXEL_PARTNER_SOURCE = 'Salesforce Commerce Cloud (Demandware) Bazaarvoice LINK cartridge 18.2.0';

export const BV_BOT_UA_PATTERN = /msnbot|googlebot|teoma|bingbot|yandexbot|yahoo/i;

const CLIENT_NAME_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;
const LOCALE_PATTERN = /^[a-z]{2}_[a-zA-Z]{2}$/;

export function isValidBvClientName(value: string | undefined): value is string {
    return typeof value === 'string' && CLIENT_NAME_PATTERN.test(value);
}

export function isValidBvLocale(value: string | undefined): value is string {
    return typeof value === 'string' && LOCALE_PATTERN.test(value);
}

export function isBazaarvoiceEnabled(config: BazaarvoiceConfig | undefined): config is BazaarvoiceConfig {
    return Boolean(config?.enabled) && isValidBvClientName(config?.clientName);
}

export function getBvDeploymentZoneId(config: Pick<BazaarvoiceConfig, 'deploymentZone'>): string {
    const zone = config.deploymentZone?.trim() || BV_DEFAULT_ZONE;
    return zone.toLowerCase().replace(/ /g, '_');
}

export function getBvScoutUrl(config: BazaarvoiceConfig | undefined): string | undefined {
    if (!isBazaarvoiceEnabled(config)) {
        return undefined;
    }
    const locale = isValidBvLocale(config.locale) ? config.locale : BV_DEFAULT_LOCALE;
    const env = config.environment === 'production' ? 'production' : 'staging';
    return `${BV_LOADER_HOST}/deployments/${config.clientName}/${getBvDeploymentZoneId(config)}/${env}/${locale}/bv.js`;
}

export function getBvSeoHost(config: Pick<BazaarvoiceConfig, 'environment'>): string {
    return config.environment === 'production' ? BV_SEO_HOST_PRODUCTION : BV_SEO_HOST_STAGING;
}

export function getBazaarvoiceCspOrigins(): string[] {
    return [
        BV_LOADER_HOST,
        BV_API_HOST_STAGING,
        BV_API_HOST_PRODUCTION,
        BV_SEO_HOST_STAGING,
        BV_SEO_HOST_PRODUCTION,
        'https://network.bazaarvoice.com',
        'https://network-stg.bazaarvoice.com',
    ];
}
